/** Maps deprecated dataset paths in older catalogs to canonical fixture filenames. */
export const DATA_URL_ALIASES: Record<string, string> = {
  "data/bar_chart_data.json": "data/performance_annualized_returns_bar.json",
  "data/calendar_year_returns_bar_data.json": "data/performance_calendar_year_returns_bar.json",
  "data/line_chart_data.json": "data/performance_cumulative_returns_100k_line.json",
  "data/horizontal_bar_chart_data.json": "data/portfolio_sector_allocation_bar.json",
  "data/average_returns_data.json": "data/performance_annualized_returns_summary.json",
  "data/statistics_data.json": "data/portfolio_statistics.json",
  "data/holdings_data.json": "data/portfolio_holdings.json",
};

import type { SourceDefinition } from "./types.js";

/** Maps deprecated source ids to canonical catalog ids. */
export const SOURCE_ID_ALIASES: Record<string, string> = {
  "bar-chart-demo": "performance_annualized_returns_bar",
  "line-chart-demo": "performance_cumulative_returns_100k_line",
  "average-returns-demo": "performance_annualized_returns_summary",
  "horizontal-bar-chart-demo": "portfolio_sector_allocation_bar",
  "statistics-demo": "portfolio_statistics",
  "holdings-demo": "portfolio_holdings",
};

/**
 * Sources added after older deployed catalogs; used when `sources.json` lacks a row
 * but the dataset exists at `data/<canonicalId>.json`.
 */
export const CANONICAL_SOURCE_FALLBACKS: Record<string, SourceDefinition> = {
  performance_calendar_year_returns_bar: {
    id: "performance_calendar_year_returns_bar",
    url: "data/performance_calendar_year_returns_bar.json",
    mode: "BarChart",
  },
  portfolio_market_cap_allocation_bar: {
    id: "portfolio_market_cap_allocation_bar",
    url: "data/portfolio_market_cap_allocation_bar.json",
    mode: "HorizontalBarChart",
  },
};

const warnedDeprecated = new Set<string>();

/** Resolve a source id to its canonical catalog id (identity if not aliased). */
export function resolveSourceId(id: string): string {
  return SOURCE_ID_ALIASES[id] ?? id;
}

/** Resolve a catalog `url` to the current dataset path when fixtures were renamed. */
export function resolveDatasetUrl(url: string): string {
  return DATA_URL_ALIASES[url] ?? url;
}

/** Canonical dataset path for a source id (`data/<id>.json`). */
export function canonicalDatasetUrl(canonicalId: string): string {
  return `data/${canonicalId}.json`;
}

/** Deprecated catalog id that maps to a canonical id (for legacy `sources.json` rows). */
export function deprecatedIdForCanonical(canonicalId: string): string | undefined {
  for (const [deprecated, canonical] of Object.entries(SOURCE_ID_ALIASES)) {
    if (canonical === canonicalId) {
      return deprecated;
    }
  }
  return undefined;
}

/** Log once per deprecated id per session when an alias is used. */
export function warnDeprecatedSourceId(requestedId: string, canonicalId: string): void {
  if (requestedId === canonicalId || warnedDeprecated.has(requestedId)) {
    return;
  }
  warnedDeprecated.add(requestedId);
  console.warn(
    `nten-data: source id "${requestedId}" is deprecated; use "${canonicalId}" instead.`
  );
}
